import { useEffect, useState } from "react"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  setModalData,
  setState,
  setActiveTrade,
  forgetDeclinedTrade,
} from "@/store/globalSlice"
import { getUserCreds } from "@/utils/utils"
import { isTradeDraftStillValid } from "@/utils/tradeHelpers"
import { allPieces } from "@/utils/data"
import Money from "@/Components/Money"
import ModalCloseButton from "@/Components/ModalCloseButton"

const TradePlayerChooser: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const tempTradeData = useAppSelector((state) => state.global.tempTradeData)
  const lastDeclinedTrades = useAppSelector(
    (state) => state.global.lastDeclinedTrades
  )

  // Phone sizing. Every size in this drawer is rem-based, and index.css halves
  // the root font size below 1000px — exactly the screens `isMobile` covers —
  // so the whole chooser was landing there at half the size it was drawn at: a
  // 9px title, 6px meta lines, a 16px close button. The mobile steps below are
  // raw px, which that scaling can't touch, the same way CreateTradeModal sizes
  // the drawer this one hands over to.
  const isMobile = useAppSelector((state) => state.global.isMobile)

  const visible = tempTradeData?.status === "choosing-player"

  // Drawer slide animation, same as CreateTradeModal (which takes over this same
  // slot once a player is picked). `open` flips to true one frame after the
  // drawer becomes visible (slide in) and back to false on close (slide out);
  // the trade state is only cleared once the slide-out has finished.
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!visible) {
      setOpen(false)
      return
    }
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [visible])

  if (!visible) return null

  const players = myGame?.situation?.players || []
  const myOrder = players.find((p) => p.id === getUserCreds().email)?.order
  const activeTrades = myGame?.situation?.activeTrades || []
  const otherPlayers = players.filter(
    (p) => p.id !== getUserCreds().email && !p.isBankrupt
  )

  const close = () => {
    setOpen(false)
    window.setTimeout(() => {
      dispatch(setState({ tempTradeData: { status: undefined } as any }))
    }, 300)
  }

  const choosePlayer = (order: PlayerOrder) => {
    // No order means we aren't in this game's player list at all, which would
    // build an offer with nobody making it.
    if (myOrder === undefined) return

    // If this player declined an offer earlier, re-open that offer as the
    // starting draft — retrying a rejected trade with one tweak is the common
    // case, and rebuilding it from nothing every time is the tedious part.
    // Only when it would still go through: properties can have changed hands or
    // gained houses and cash can have been spent since it was turned down, and
    // a draft that can't be offered is worse than a blank one.
    const declined = lastDeclinedTrades?.[order]
    const restorable =
      declined?.offeringPlayerOrder === myOrder &&
      isTradeDraftStillValid(myGame, declined)
    if (declined && !restorable) dispatch(forgetDeclinedTrade(order))

    // Every field spelled out rather than merged over whatever happens to be in
    // tradeData. setActiveTrade merges when a draft exists and assigns wholesale
    // when one doesn't, so a partial payload here produced a different trade
    // depending on timing — with no offering player at all if the previous draft
    // had just been cleared, which renders no drawer while still dimming the
    // board. The identity fields matter too: carried over from a trade that was
    // merely *viewed*, a stale `id` makes the server treat this new offer as one
    // it already has and quietly drop it.
    dispatch(
      setActiveTrade({
        offeringPlayerOrder: myOrder,
        offeredPlayerOrder: order,
        // Copied, so editing the draft never writes back into the remembered
        // trade through a shared array reference.
        givenProperties: restorable ? [...declined!.givenProperties] : [],
        takenProperties: restorable ? [...declined!.takenProperties] : [],
        moneyGiven: restorable ? declined!.moneyGiven : 0,
        moneyTaken: restorable ? declined!.moneyTaken : 0,
        // A draft is not an offer until it is sent: no id, no timestamp, and
        // none of the markers that describe where an offer came from.
        id: undefined,
        offeredAt: undefined,
        origin: undefined,
        replacesTradeId: undefined,
      })
    )
    // Swap straight over to the trade drawer — it plays its own slide-in, so
    // this one is unmounted immediately rather than sliding out first.
    dispatch(setState({ tempTradeData: { status: undefined } as any }))
    dispatch(setModalData({ trade: { visible: true } }))
  }

  return (
    <div
      data-testid="trade.player-chooser"
      className={classNames(
        // Bottom-left docked compact drawer — same slot, width and skin as
        // CreateTradeModal so choosing a player flows straight into the offer.
        // Safe area handled exactly as CreateTradeModal does it — flush to the
        // screen corner, insets taken as padding so the surface runs under the
        // cutouts and only the content sits inside them. The chooser hands
        // straight over to that drawer, so the two must occupy the same box to
        // the pixel or it visibly jumps on the way through.
        "fixed left-0 bottom-0 z-[120] flex flex-col",
        "pl-[var(--safe-left)] pb-[var(--safe-bottom)]",
        "w-[calc(var(--safe-left)_+_(var(--play-w)_-_100vh)/2)]",
        "max-h-[calc(var(--play-h)*0.82_+_var(--safe-bottom))]",
        "rounded-tr-2xl border-r border-t border-white/10 overflow-hidden",
        "bg-[#14121b]",
        "shadow-[8px_0_24px_rgba(0,0,0,0.6)]",
        "transition-transform duration-300 ease-out",
        open ? "translate-x-0" : "-translate-x-full"
      )}
    >
      {/* Header */}
      <div
        className={classNames(
          "relative flex items-center shrink-0 border-b border-white/10",
          isMobile ? "gap-[8px] px-[10px] py-[8px]" : "gap-2.5 px-3.5 py-2.5"
        )}
      >
        {/* The icon badge is desktop-only. This header has one line to fit a
            title and a close button into (var(--play-w) - 100vh)/2 — roughly
            140-200px depending on the phone, and about 15px narrower than it
            was before the board took the full screen height (Board.tsx) — and
            at the sizes below, the badge and its gap are 36px of that.
            "Trade with" is 80px of Poppins on its own, so keeping the badge is
            the difference between the title reading and it truncating to
            "Trade wi…". The drawer is already amber-accented throughout; the
            title is the part that says anything. */}
        {!isMobile && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 ring-1 ring-amber-300/25">
            <i className="fa-solid fa-right-left text-amber-300 text-base" />
          </span>
        )}
        <h2
          className={classNames(
            "min-w-0 flex-1 font-poppins font-semibold text-white leading-tight truncate",
            isMobile ? "text-[14px]" : "text-lg"
          )}
        >
          Trade with
        </h2>
        <ModalCloseButton
          onClick={close}
          data-testid="trade.player-chooser.close-btn"
          // The override that keeps this button from dwarfing the header is
          // itself rem (`!w-8`), so on a phone it was shrinking a 44px target
          // down to 16px — smaller than the icon drawn inside it. Restated in
          // px, and a step up from the header's other chrome: backing out is
          // the one thing done here without picking anybody, and it has to be
          // hittable with a thumb.
          className={classNames(
            "shrink-0",
            isMobile ? "!w-[40px] !h-[40px]" : "!w-8 !h-8"
          )}
        />
      </div>

      <div
        className={classNames(
          "flex items-center shrink-0",
          isMobile ? "gap-[6px] px-[10px] pt-[7px]" : "gap-2 px-3.5 pt-2"
        )}
      >
        <i
          className={classNames(
            "fa-solid fa-hand-pointer text-white/60 shrink-0",
            isMobile ? "text-[10px]" : "text-sm"
          )}
        />
        <span
          className={classNames(
            "text-white/70 font-poppins leading-snug",
            isMobile ? "text-[11px]" : "text-sm"
          )}
        >
          Choose a player to make an offer.
        </span>
      </div>

      {/* Players */}
      <div
        className={classNames(
          "flex flex-col min-h-0 flex-1 overflow-y-auto",
          isMobile ? "gap-[6px] px-[8px] py-[8px]" : "gap-2 px-3.5 py-2.5"
        )}
      >
        {otherPlayers.length === 0 && (
          <div
            className={classNames(
              "text-center text-white/50 font-poppins",
              isMobile ? "text-[11px] py-[14px]" : "text-sm py-4"
            )}
          >
            No players available to trade with.
          </div>
        )}
        {otherPlayers.map((player) => {
          const user = myGame?.players.find((u) => u.email === player.id)
          const piece = allPieces.find((pc) => pc.id === player.pieceId)
          const propertyCount = player.properties?.length || 0
          const name = user?.username || "Player"
          // A player can only have one pending offer to a given opponent at a
          // time — they must withdraw the existing one before making another.
          const hasPendingTrade = activeTrades.some(
            (t) =>
              t.offeringPlayerOrder === myOrder &&
              t.offeredPlayerOrder === player.order
          )
          return (
            <button
              key={player.order}
              onClick={() => !hasPendingTrade && choosePlayer(player.order)}
              disabled={hasPendingTrade}
              data-testid={`trade.select-option-${player.order}`}
              title={
                hasPendingTrade
                  ? `You already have a pending trade with ${name}. Withdraw it first to create a new one.`
                  : undefined
              }
              className={classNames(
                "group flex items-center w-full shrink-0 border transition-all duration-150",
                isMobile
                  ? "gap-[6px] px-[7px] py-[6px] rounded-[10px]"
                  : "gap-2.5 px-3 py-2.5 rounded-xl",
                hasPendingTrade
                  ? "bg-white/[0.02] border-white/5 opacity-70 cursor-not-allowed"
                  : "bg-[#1c1927] border-white/10 hover:border-amber-400 hover:bg-amber-400/[0.08] active:scale-[0.99]"
              )}
            >
              {/* Avatar */}
              <div
                className={classNames(
                  "flex items-center justify-center shrink-0 rounded-full bg-black/40 ring-1 transition-all",
                  isMobile ? "h-[28px] w-[28px]" : "h-10 w-10",
                  hasPendingTrade
                    ? "ring-white/10"
                    : "ring-white/15 group-hover:ring-amber-400/60"
                )}
              >
                {piece?.imgSrc && (
                  <img
                    className={classNames(
                      "object-contain",
                      isMobile ? "h-[18px] w-[18px]" : "h-6 w-6"
                    )}
                    src={piece.imgSrc}
                  />
                )}
              </div>

              {/* Name + meta */}
              <div className="flex-1 min-w-0 text-left">
                <div
                  className={classNames(
                    "font-poppins font-semibold text-white truncate",
                    isMobile ? "text-[12px]" : "text-base"
                  )}
                >
                  {name}
                </div>
                {hasPendingTrade ? (
                  <div
                    className={classNames(
                      "flex items-start gap-1 text-amber-300/90 font-poppins mt-0.5 leading-snug",
                      isMobile ? "text-[9px]" : "text-xs"
                    )}
                  >
                    <i
                      className={classNames(
                        "fa-solid fa-circle-exclamation mt-0.5 shrink-0",
                        isMobile ? "text-[8px]" : "text-[0.6875rem]"
                      )}
                    ></i>
                    <span>Offer already pending — withdraw it first.</span>
                  </div>
                ) : (
                  <div
                    className={classNames(
                      "flex items-center font-poppins",
                      isMobile
                        ? "mt-[3px] gap-[5px] text-[9px] leading-none"
                        : "mt-0.5 gap-1 text-white/40 text-xs"
                    )}
                  >
                    {/* The cash sits on this line on a phone rather than out on
                        the right. On the right it and the chevron take about
                        50px off a row that is only ~140px wide to begin with,
                        and the name — the thing being chosen — is the flexible
                        column that pays for it: it was down to seven or eight
                        characters. Moved down here it costs nothing, because
                        this line is short and the row is already two lines
                        tall. */}
                    {isMobile && (
                      <Money
                        money={player.money}
                        className="shrink-0 text-amber-200 font-poppins font-bold gap-0.5 text-[13px] leading-none group-hover:text-amber-100 transition-colors"
                      />
                    )}
                    <span
                      className={classNames(
                        "flex items-center",
                        isMobile ? "gap-[3px] text-white/40" : "gap-1"
                      )}
                    >
                      <i
                        className={classNames(
                          "fa-solid fa-building",
                          isMobile ? "text-[8px]" : "text-[0.6875rem]"
                        )}
                      ></i>
                      {/* Just the number next to the icon on a phone: spelled
                          out, "3 properties" is 54px of the ~90px this column
                          has, and it would push the cash off the line it was
                          moved onto. */}
                      <span>
                        {propertyCount}
                        {!isMobile &&
                          ` ${
                            propertyCount === 1 ? "property" : "properties"
                          }`}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              {hasPendingTrade ? (
                <i
                  className={classNames(
                    "fa-solid fa-lock text-white/25 shrink-0",
                    isMobile ? "text-[12px]" : "text-base"
                  )}
                ></i>
              ) : (
                <>
                  {!isMobile && (
                    <Money
                      money={player.money}
                      className="shrink-0 text-amber-200 font-poppins font-bold gap-0.5 text-xl leading-none group-hover:text-amber-100 transition-colors"
                      iconClass="h-1/2"
                    />
                  )}

                  <i
                    className={classNames(
                      "fa-solid fa-chevron-right text-white/20 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all shrink-0",
                      isMobile ? "text-[10px]" : "text-sm"
                    )}
                  ></i>
                </>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default TradePlayerChooser
